import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation} from '@angular/core';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { HcModal, HcSort, HcTableDataSource, ModalOptions, ModalService, PaginationComponent, SimpleModalOptions } from '@healthcatalyst/cashmere';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { StatusElement, StatusService } from 'src/app/shared/services/status.service';

/** Admin list of statuses to edit */
@Component({
    selector: 'pd-admin-stauses',
    templateUrl: './admin-statuses.component.html',
    styleUrls: ['./admin-statuses.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminStatusesComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<StatusElement> = new HcTableDataSource();
    loading = true;
    loadError = false;
    adminStatusesDisplayedColumns: string[] = ['id', 'name', 'description', 'actions'];
    adminStatusesPageSize: number = 10;
    private modalRef: HcModal<TemplateRef<any>>;
    adminStatusForm = new FormGroup({
        id: new FormControl(0, {nonNullable: true}), 
        name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        description: new FormControl('', {nonNullable: true, validators: [Validators.required]})
    });
    quillConfig = {
        toolbar: [['bold', 'italic', 'underline', 'strike'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean'], ['link']]
    };
    quillStyles = {
        'font-family': "'Noto Sans', 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif"
    };

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get adminStatusesListLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor( private statusService: StatusService, private modalService: ModalService ) {}

    ngOnInit(): void {
        this.statusService.statusListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;
                this.dataSource = new HcTableDataSource(list);
                if ( this.sort && this.paginator ) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                }
            } else {
                this.loadError = this.statusService.loadFailed;
                if ( !this.loadError ) {
                    this.loadStatuses();
                } else {
                    this.loading = false;
                }
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    loadStatuses(): void {
        this.statusService.refresh();
    }

    applyFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }

    openAddEditModal(content: TemplateRef<any>, edit: boolean, status?: StatusElement) {
        const options: ModalOptions = {
            size: 'md',
            data: {
                status: status,
                edit: edit
            }
        };
        this.adminStatusForm.reset();

        if(status) {
            this.adminStatusForm.patchValue({
                id: status.statusID,
                name: status.name,
                description: status.description  
             });
        } 
        this.modalRef = this.modalService.open(content, options);
    }


    editStatus() {
        const target: StatusElement = {
            statusID: this.adminStatusForm.controls['id'].value,
            name: this.adminStatusForm.controls['name'].value,
            description: this.adminStatusForm.controls['description'].value
        };
        this.statusService.update( target );
        this.modalRef.close();
    }

    addStatus() {
        const target: StatusElement = {
            statusID: -1,
            name: this.adminStatusForm.controls['name'].value,
            description: this.adminStatusForm.controls['description'].value
        };
        this.statusService.add( target );
        this.modalRef.close();
    }

    removeStatus( element: StatusElement ) {
        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the status: <strong>' + element.name + '</strong>?<p>',
            headerText: 'Delete Status',
            confirmButtonText: 'Delete Status',
            icon: ''
        };
        this.modalService.confirmDestructive(options)
            .pipe(take(1),filter(confirmed => confirmed))
            .subscribe(() => {
                this.statusService.remove( element );
            });
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}